import React, { useState, useMemo } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  Tabs, 
  Tab,
  useTheme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Chart } from 'react-google-charts';
import parseChartTimestamp from '../../../utils/parseChartTimestamp';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: 16,
    borderRadius: 12,
    height: '100%',
    minHeight: 400,
  },
  chartContainer: {
    height: 300,
    width: '100%',
    marginTop: theme.spacing(2),
  },
  tabContainer: {
    borderBottom: '1px solid #e0e0e0',
    marginBottom: theme.spacing(2),
  },
  tab: {
    minWidth: 80,
    fontSize: '0.875rem',
  },
  summaryBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : theme.palette.grey[50],
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
  },
  metricValue: {
    fontWeight: 700,
    fontSize: '1.75rem',
    lineHeight: 1.2,
  },
  metricLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  }
}));

const UserClickActivityChart = ({ clickStatsData, dateRange = '30days', getTranslation = (text) => text, preferredLanguage = 'EN' }) => {
  const classes = useStyles();
  const theme = useTheme();
  
  // Map global dateRange to period index
  const getPeriodIndex = () => {
    switch (dateRange) {
      case 'today': return 0;
      case '7days': return 1;
      case '30days': return 2;
      default: return 2; // Default to 30 days
    }
  };
  
  const selectedPeriod = getPeriodIndex();

  // Process data for charting based on global dateRange
  const chartData = useMemo(() => {
    if (!clickStatsData) return null;

    const periods = ['today', 'sevenDays', 'thirtyDays'];
    const currentPeriod = periods[selectedPeriod];
    const periodData = clickStatsData[currentPeriod];
    
    console.log('UserClickActivityChart - Raw clickStatsData:', clickStatsData);
    console.log('UserClickActivityChart - Current period:', currentPeriod);
    console.log('UserClickActivityChart - Period data:', periodData);

    const dataRows = Array.isArray(periodData?.data) ? periodData.data : [];
    if (dataRows.length === 0) {
      return null;
    }

    // Get user emails for column headers — default to empty if missing
    const emails = Array.isArray(periodData.emails) ? periodData.emails : [];
    const userNames = emails.map(email =>
      typeof email === 'string' ? email.split('@')[0] : 'user'
    );
    
    // Transform data for Google Charts
    // Each data point: [timestamp, user1_clicks, user2_clicks, user3_clicks, user4_clicks]
    const headers = ['Time', ...userNames, 'Total Activity'];
    const expectedCols = emails.length + 1; // timestamp + one col per user
    
    const chartRows = dataRows
      .filter(row => Array.isArray(row) && row.length >= 1) // must have at least a timestamp
      .map(dataPoint => {
        const [timestamp, ...userClicks] = dataPoint;
        const mode = currentPeriod === 'today' ? 'hourly' : 'daily';
        const formattedTime = parseChartTimestamp(timestamp, mode);
      
        // Pad or trim userClicks to match expected column count
        const normalizedClicks = emails.map((_, i) => Number(userClicks[i]) || 0);
        const totalActivity = normalizedClicks.reduce((sum, c) => sum + c, 0);
      
        return [formattedTime, ...normalizedClicks, totalActivity];
      });

    return [headers, ...chartRows];
  }, [clickStatsData, dateRange, selectedPeriod]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!chartData || chartData.length <= 1) {
      return { totalClicks: 0, avgDaily: 0, peakActivity: 0 };
    }

    const dataRows = chartData.slice(1); // Remove headers
    const totalActivityIndex = chartData[0].length - 1; // Last column is total activity
    const totalClicks = dataRows.reduce((sum, row) => sum + (row[totalActivityIndex] || 0), 0);
    const avgDaily = Math.round(totalClicks / dataRows.length);
    const peakActivity = Math.max(...dataRows.map(row => row[totalActivityIndex] || 0));

    return { totalClicks, avgDaily, peakActivity };
  }, [chartData]);

  // Chart options
  const getChartOptions = () => {
    const periods = ['today', 'sevenDays', 'thirtyDays'];
    const currentPeriod = periods[selectedPeriod];
    
    return {
      backgroundColor: 'transparent',
      chartArea: { 
        left: 60, 
        top: 30, 
        width: '65%', 
        height: '70%' 
      },
      colors: ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0'], // Dynamic colors for users + total
      curveType: 'function', // This creates smooth lines
      lineWidth: 3,
      pointSize: 4,
      pointShape: 'circle',
      legend: { 
        position: 'right',
        alignment: 'center',
        textStyle: { 
          color: theme.palette.text.primary,
          fontSize: 11
        }
      },
      hAxis: {
        textStyle: { 
          color: theme.palette.text.secondary,
          fontSize: 11
        },
        format: currentPeriod === 'today' ? 'HH:mm' : 'MMM d',
        slantedText: currentPeriod !== 'today',
        slantedTextAngle: 30,
        gridlines: { 
          color: theme.palette.divider,
          count: currentPeriod === 'today' ? 8 : 5
        }
      },
      vAxis: {
        textStyle: { 
          color: theme.palette.text.secondary,
          fontSize: 11
        },
        minValue: 0,
        gridlines: { 
          color: theme.palette.divider
        }
      },
      animation: {
        startup: true,
        duration: 1000,
        easing: 'out',
      },
    };
  };

  const getPeriodLabel = () => {
    switch (dateRange) {
      case 'today': return getTranslation('Today (Hourly)', preferredLanguage);
      case '7days': return getTranslation('7 Days', preferredLanguage);
      case '30days': return getTranslation('30 Days', preferredLanguage);
      default: return getTranslation('30 Days', preferredLanguage);
    }
  };

  if (!clickStatsData) {
    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          {getTranslation('User Click Activity Trends', preferredLanguage)}
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="center" style={{ height: 300 }}>
          <Typography variant="body2" color="textSecondary">
            {getTranslation('Click activity data not available', preferredLanguage)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
        {getTranslation('User Click Activity Trends', preferredLanguage)}
      </Typography>
      
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        {getTranslation('Track user click patterns and security interactions over time', preferredLanguage)} • {getPeriodLabel()}
      </Typography>

      {/* Chart */}
      <Box className={classes.chartContainer}>
        {chartData && chartData.length > 1 ? (
          <Chart
            chartType="LineChart"
            data={chartData}
            options={getChartOptions()}
            width="100%"
            height="100%"
            loader={<div>{getTranslation('Loading chart...', preferredLanguage)}</div>}
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" style={{ height: '100%' }}>
            <Typography variant="body2" color="textSecondary">
              {getTranslation('No activity data available for', preferredLanguage)} {getPeriodLabel()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Summary Metrics - BIGGER AND MORE PROMINENT */}
      {chartData && chartData.length > 1 && (
        <Box className={classes.summaryBox}>
          <Box textAlign="center" style={{ flex: 1 }}>
            <Typography className={classes.metricLabel} color="textSecondary">
              {getTranslation('Total Clicks', preferredLanguage)}
            </Typography>
            <Typography className={classes.metricValue} style={{ color: '#2196f3' }}>
              {summaryMetrics.totalClicks.toLocaleString()}
            </Typography>
          </Box>
          <Box textAlign="center" style={{ flex: 1 }}>
            <Typography className={classes.metricLabel} color="textSecondary">
              {getTranslation('Avg Per Period', preferredLanguage)}
            </Typography>
            <Typography className={classes.metricValue} style={{ color: '#4caf50' }}>
              {summaryMetrics.avgDaily.toLocaleString()}
            </Typography>
          </Box>
          <Box textAlign="center" style={{ flex: 1 }}>
            <Typography className={classes.metricLabel} color="textSecondary">
              {getTranslation('Peak Activity', preferredLanguage)}
            </Typography>
            <Typography className={classes.metricValue} style={{ color: '#ff9800' }}>
              {summaryMetrics.peakActivity.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default UserClickActivityChart;